import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Option 4: Neon/Cyberpunk Style
 * Bright colors, glow effects, futuristic
 */
const NeonProgress = ({ currentStep = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const steps = [
    { id: 1, name: 'Business Context' },
    { id: 2, name: 'Table Selection' },
    { id: 3, name: 'Domain Classification' },
    { id: 4, name: 'Data Profiling' },
    { id: 5, name: 'Relationship Identification' },
    { id: 6, name: 'Business Glossary' },
  ];

  const currentStepInfo = steps[currentStep - 1];
  const progressPercentage = ((currentStep - 1) / 5) * 100;

  return (
    <div
      className="absolute top-4 left-4 z-50 transition-all duration-300"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Compact View */}
      {!isExpanded && (
        <div 
          className="bg-black border-2 border-cyan-400 rounded px-3 py-2"
          style={{
            boxShadow: '0 0 20px rgba(34, 211, 238, 0.5), inset 0 0 20px rgba(34, 211, 238, 0.1)'
          }}
        >
          <div className="flex items-center gap-2.5">
            <div 
              className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"
              style={{ boxShadow: '0 0 10px rgba(34, 211, 238, 0.8)' }}
            ></div>
            <div className="flex flex-col">
              <span 
                className="text-xs font-bold text-cyan-400"
                style={{ textShadow: '0 0 10px rgba(34, 211, 238, 0.8)' }}
              >
                [{currentStep}/{steps.length}]
              </span>
              <span className="text-[10px] text-cyan-300/70">
                {currentStepInfo?.name}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div 
          className="bg-black border-2 border-cyan-400 rounded px-4 py-3 min-w-[300px]"
          style={{
            boxShadow: '0 0 30px rgba(34, 211, 238, 0.6), inset 0 0 30px rgba(34, 211, 238, 0.1)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-cyan-400/30">
            <span 
              className="text-xs font-bold text-cyan-400 uppercase tracking-wider"
              style={{ textShadow: '0 0 10px rgba(34, 211, 238, 0.8)' }}
            >
              ⚡ Progress
            </span>
            <span 
              className="text-xs font-bold text-pink-400"
              style={{ textShadow: '0 0 10px rgba(236, 72, 153, 0.8)' }}
            >
              {currentStep}/{steps.length}
            </span>
          </div>

          {/* Neon Progress Bar */}
          <div className="mb-4">
            <div className="h-2 bg-gray-900 rounded-full overflow-hidden border border-cyan-400/30">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 via-blue-500 to-pink-500 transition-all duration-700 ease-out"
                style={{ 
                  width: `${progressPercentage}%`,
                  boxShadow: '0 0 15px rgba(34, 211, 238, 0.8)'
                }}
              />
            </div>
          </div>

          {/* Steps List */}
          <div className="space-y-2">
            {steps.map((step) => {
              const isComplete = step.id < currentStep;
              const isCurrent = step.id === currentStep;
              const isPending = step.id > currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-2.5 text-xs transition-all duration-300 ${
                    isCurrent ? 'scale-105' : ''
                  }`}
                >
                  {isComplete && (
                    <>
                      <div 
                        className="w-5 h-5 rounded-sm bg-emerald-400 flex items-center justify-center"
                        style={{ boxShadow: '0 0 10px rgba(52, 211, 153, 0.6)' }}
                      >
                        <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-emerald-400/60 line-through">{step.name}</span>
                    </>
                  )}
                  {isCurrent && (
                    <>
                      <div 
                        className="w-5 h-5 rounded-sm bg-cyan-400 flex items-center justify-center animate-pulse"
                        style={{ boxShadow: '0 0 15px rgba(34, 211, 238, 0.8)' }}
                      >
                        <div className="w-2 h-2 bg-black rounded-full"></div>
                      </div>
                      <span 
                        className="text-cyan-400 font-bold"
                        style={{ textShadow: '0 0 10px rgba(34, 211, 238, 0.6)' }}
                      >
                        {step.name}
                      </span>
                    </>
                  )}
                  {isPending && (
                    <>
                      <div className="w-5 h-5 rounded-sm border border-gray-700 flex items-center justify-center">
                        <div className="w-2 h-2 bg-gray-700 rounded-full" />
                      </div>
                      <span className="text-gray-600">{step.name}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Animated Border Effect */}
          <div className="absolute inset-0 rounded pointer-events-none">
            <div 
              className="absolute inset-0 rounded animate-pulse"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.3), transparent)',
                animation: 'slide 2s linear infinite'
              }}
            ></div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default NeonProgress;